import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, AlertCircle, TrendingUp, Compass, UserCheck, Package2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

const Section = ({ icon: Icon, title, subtitle, items, accent = "slate", testid }) => (
  <div className="bg-card border border-border rounded-xl p-8" data-testid={testid}>
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-9 h-9 rounded-lg bg-${accent}-100 text-${accent}-700 flex items-center justify-center`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <h3 className="font-display text-lg font-semibold text-foreground">{title}</h3>
        {subtitle && <div className="text-xs text-muted-foreground">{subtitle}</div>}
      </div>
    </div>
    <ul className="space-y-3 text-foreground/80 leading-relaxed">
      {items?.map((t, i) => (
        <li key={i} className="flex gap-3">
          <span className={`mt-2 w-1.5 h-1.5 rounded-full bg-${accent}-500 flex-shrink-0`} />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  </div>
);

const Tag = ({ children }) => (
  <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-muted text-foreground/80 mr-2 mb-2">
    {children}
  </span>
);

export default function DirectorPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/director-comercial").then((r) => setReport(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <div>
      <header className="flex items-end justify-between mb-10 gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Informe ejecutivo</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-foreground mt-2">Director Comercial</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Un resumen ejecutivo generado a partir de sus ventas. Lea en menos de cinco minutos que ocurrio, que preocupa y que decisiones tomar.
          </p>
        </div>
        <Button variant="outline" onClick={load} className="h-11" data-testid="director-refresh">
          <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
        </Button>
      </header>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-48 rounded-xl shimmer" />
          ))}
        </div>
      ) : report ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Section testid="section-ocurrio" icon={Sparkles} title="Que ocurrio esta semana" accent="slate" items={report.ocurrio} />
          <Section testid="section-preocupa" icon={AlertCircle} title="Que preocupa" accent="rose" items={report.preocupa} />
          <Section testid="section-oportunidades" icon={TrendingUp} title="Oportunidades" accent="emerald" items={report.oportunidades} />
          <Section testid="section-decisiones" icon={Compass} title="Decisiones para la proxima semana" accent="blue" items={report.decisiones} />

          <div className="bg-card border border-border rounded-xl p-8" data-testid="section-clientes-seguimiento">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center"><UserCheck className="w-4 h-4" /></div>
              <div><h3 className="font-display text-lg font-semibold text-foreground">Clientes que requieren seguimiento</h3></div>
            </div>
            <div>{report.clientes_seguimiento?.length ? report.clientes_seguimiento.map((c, i) => <Tag key={i}>{c}</Tag>) : <span className="text-muted-foreground/70 text-sm">Ninguno por ahora.</span>}</div>
          </div>

          <div className="bg-card border border-border rounded-xl p-8" data-testid="section-productos">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg bg-muted text-foreground/80 flex items-center justify-center"><Package2 className="w-4 h-4" /></div>
              <div><h3 className="font-display text-lg font-semibold text-foreground">Productos</h3></div>
            </div>
            <div className="mb-4">
              <div className="text-xs uppercase tracking-wider text-primary font-semibold mb-2">Estan creciendo</div>
              <div>{report.productos_crecen?.length ? report.productos_crecen.map((p, i) => <Tag key={i}>{p}</Tag>) : <span className="text-muted-foreground/70 text-sm">Sin crecimiento significativo.</span>}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-rose-700 font-semibold mb-2">Requieren atencion</div>
              <div>{report.productos_atencion?.length ? report.productos_atencion.map((p, i) => <Tag key={i}>{p}</Tag>) : <span className="text-muted-foreground/70 text-sm">Sin alertas.</span>}</div>
            </div>
          </div>
        </div>
      ) : null}

      {report && (
        <div className="mt-8 text-xs text-muted-foreground/70 text-center">
          Generado {new Date(report.generado).toLocaleString("es-CO")}
        </div>
      )}
    </div>
  );
}
